<script>
import storageService from "@/services/storage.service";
import Layout from "../../layouts/main";
import axios from "axios";
import AppIcon from "@/components/shared/AppIcon.vue";
import StatCard from "@/components/shared/StatCard.vue";

// Counter tiles. `key` is the field returned by /applications/all_member_count/.
const STAT_CARDS = [
  { key: "Total_Staff_Members", label: "Total Staff Members", icon: "users", to: "/staff_users" },
  { key: "Total_Applicants", label: "Total Applicants", icon: "user-plus", to: "/applicants" },
  {
    key: "Total_Approved_Applications",
    label: "Total Approved Applications",
    icon: "arrow-down-circle",
    to: "/approved_application",
  },
  {
    key: "Total_Rejected_Applications",
    label: "Total Rejected Applications",
    icon: "x-circle",
    to: "/rejected_application",
  },
  {
    key: "Total_Incomplete_Applications",
    label: "Total Incomplete Applications",
    icon: "trending-up",
    to: "/incomplete_application",
  },
  {
    key: "Total_Submitted_Applications",
    label: "Submitted Applications",
    icon: "send",
    to: "/submit",
  },
  {
    key: "Total_In_Review_Applications",
    label: "In Review Applications",
    icon: "clock",
    to: "/admin_in_review",
  },
  {
    key: "Total_Verified_Applications",
    label: "Verified Applications",
    icon: "shield-check",
    to: "/admin_verified",
  },
];

// Same order as `piechart_details` from /applications/all_application_details/.
const PIE_SLICES = [
  { label: "Approved", color: "#bdf0e0" },
  { label: "Rejected", color: "#fac9de" },
  { label: "Incomplete", color: "#dcf0c2" },
  { label: "In Review", color: "#cfe2ff" },
  { label: "In Progress", color: "#dccbfa" },
  { label: "Submitted", color: "#ffd9bc" },
  { label: "Verified", color: "#eadfd6" },
];

export default {
  name: "AdminDashboard",
  components: {
    Layout,
    AppIcon,
    StatCard,
  },
  data() {
    return {
      Total_Staff_Members: 0,
      Total_Applicants: 0,
      Total_Approved_Applications: 0,
      Total_Rejected_Applications: 0,
      Total_Incomplete_Applications: 0,
      Total_Submitted_Applications: 0,
      Total_In_Review_Applications: 0,
      Total_Verified_Applications: 0,

      statCards: STAT_CARDS,
      pieSlices: PIE_SLICES,
      countsLoading: true,
      pieSeries: [],
      pieReady: false,
    };
  },
  computed: {
    // the chart only makes sense once at least one application exists
    show_pie() {
      return this.pieSeries.reduce((a, b) => a + b, 0) > 0;
    },
    chartPending() {
      return !this.pieReady;
    },
    chartOptions() {
      return {
        chart: {
          type: "donut",
          fontFamily: "inherit",
          toolbar: { show: false },
          animations: { speed: 500 },
        },
        labels: this.pieSlices.map((s) => s.label),
        colors: this.pieSlices.map((s) => s.color),
        legend: { show: false },
        stroke: { width: 3, colors: ["#ffffff"] },
        dataLabels: {
          enabled: true,
          formatter: (val) => (val >= 1 ? `${val.toFixed(1)}%` : ""),
          dropShadow: { enabled: false },
          style: {
            fontSize: "13px",
            fontWeight: 700,
            colors: this.pieSlices.map(() => "#1d2a4d"),
          },
        },
        plotOptions: {
          pie: {
            expandOnClick: false,
            dataLabels: { minAngleToShowLabel: 8 },
            donut: { size: "46%", labels: { show: false } },
          },
        },
        states: {
          hover: { filter: { type: "lighten", value: 0.04 } },
          active: { filter: { type: "none" } },
        },
        tooltip: {
          theme: "dark",
          fillSeriesColor: false,
          style: { fontSize: "13px" },
          y: {
            formatter: (v) => `${v} application${v === 1 ? "" : "s"}`,
          },
        },
        responsive: [{ breakpoint: 600, options: { chart: { height: 260 } } }],
      };
    },
  },
  mounted() {
    this.fetchData();
    this.fetchChart();
  },
  methods: {
    fetchData() {
      this.countsLoading = true;
      axios
        .get("/applications/all_member_count/", {
          params: { token: storageService.getItem("accessToken") },
        })
        .then((response) => {
          const data = response.data;
          this.Total_Staff_Members = data.Total_Staff_Members;
          this.Total_Applicants = data.Total_Applicants;
          this.Total_Approved_Applications = data.Total_Approved_Applications;
          this.Total_Rejected_Applications = data.Total_Rejected_Applications;
          this.Total_Incomplete_Applications =
            data.Total_Incomplete_Applications;
          this.Total_Submitted_Applications = data.Total_Submitted_Applications;
          this.Total_In_Review_Applications = data.Total_In_Review_Applications;
          this.Total_Verified_Applications = data.Total_Verified_Applications;
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.countsLoading = false;
        });
    },
    // The donut chart numbers come from the applications list endpoint
    // (`piechart_details`), so a one-row page is requested just for them.
    fetchChart() {
      axios
        .get("/applications/all_application_details/", {
          params: { page: 1, page_size: 1 },
        })
        .then((response) => {
          const data = response.data || {};
          this.pieSeries = (data.piechart_details || []).map((n) => Number(n) || 0);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.pieReady = true;
        });
    },
  },
};
</script>

<template>
  <Layout>
    <section class="dash">
      <header class="ecu-page-head">
        <div>
          <h1>Dashboard</h1>
          <p class="ecu-page-head__sub">Daily Report Membership.</p>
        </div>
      </header>

      <!-- counters + membership chart -->
      <div class="dash__top">
        <div class="dash__stats">
          <StatCard
            v-for="(card, idx) in statCards"
            :key="card.key"
            :icon="card.icon"
            :label="card.label"
            :value="$data[card.key]"
            :to="card.to"
            :loading="countsLoading"
            :index="idx"
          />
        </div>

        <div class="ecu-card dash__chart">
          <h2 class="dash__chart-title">Daily Report Membership</h2>

          <div v-if="chartPending" class="dash__chart-skeleton">
            <span class="ecu-skeleton"></span>
          </div>

          <template v-else-if="show_pie">
            <div class="dash__donut">
              <apexchart
                class="apex-charts"
                height="300"
                type="donut"
                dir="ltr"
                :series="pieSeries"
                :options="chartOptions"
              >
              </apexchart>
              <span class="dash__donut-hole" aria-hidden="true">
                <AppIcon name="users" :size="38" :stroke="1.7" />
              </span>
            </div>

            <ul class="dash__legend">
              <li v-for="slice in pieSlices" :key="slice.label">
                <span class="dash__dot" :style="{ background: slice.color }"></span>
                {{ slice.label }}
              </li>
            </ul>
          </template>

          <div v-else class="ecu-state">
            <span class="ecu-state__icon"><AppIcon name="inbox" :size="26" /></span>
            <p>
              Charts for Accepted, Rejected and incomplete data will be shown
              here.
            </p>
          </div>
        </div>
      </div>
    </section>
  </Layout>
</template>

<style scoped>
.dash {
  display: flex;
  flex-direction: column;
  gap: 22px;
}
.dash .ecu-page-head {
  margin-bottom: 0;
}

/* ---------- counters + chart ---------- */
.dash__top {
  display: grid;
  grid-template-columns: minmax(0, 1.22fr) minmax(0, 1fr);
  gap: 22px;
  align-items: stretch;
}
.dash__stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: 1fr;
  gap: 14px;
}

.dash .dash__chart {
  display: flex;
  flex-direction: column;
  padding: 24px;
}
.dash__chart-title {
  margin: 0 0 6px;
  text-align: center;
  font-size: var(--ecu-fs-h2);
  font-weight: var(--ecu-fw-heading);
  letter-spacing: -0.01em;
  color: var(--ecu-navy, #0b1f4b);
}
.dash__donut {
  position: relative;
}
.dash__donut-hole {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 96px;
  height: 96px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  color: var(--ecu-orange, #f97316);
  box-shadow: 0 8px 26px rgba(20, 40, 90, 0.14);
  pointer-events: none;
}
.dash__legend {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px 22px;
}
.dash__legend li {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: var(--ecu-fs-body);
  color: var(--ecu-ink, #1d2a4d);
}
.dash__dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(11, 31, 75, 0.06);
}
.dash__chart-skeleton {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}
.dash__chart-skeleton .ecu-skeleton {
  width: 250px;
  height: 250px;
  border-radius: 50%;
}

/* ---------- responsive ---------- */
@media (max-width: 991px) {
  .dash__top {
    grid-template-columns: minmax(0, 1fr);
  }
}
@media (max-width: 575px) {
  .dash__stats {
    grid-template-columns: minmax(0, 1fr);
  }
  .dash .dash__chart {
    padding: 18px 14px;
  }
  .dash__donut-hole {
    width: 76px;
    height: 76px;
  }
}
</style>